import React, { useMemo } from "react";

import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, List, ListItem, ListItemText, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useFormatDateHook } from "hooks/shared-hooks/use-format-date-hook";
import { useTranslation } from "react-i18next";
import { useDealDefaultCurrencySelect } from "store/selectors/deal-selectors/deal-selectors";
import { DebtSegment } from "store/types";

import { debtIndexNames } from "constants/debt";
import { currencySign } from "../../constants";
import { formatNumber } from "../../utils";

const PAYMENT_TERM_I18N: Record<string, string> = {
  fixed_principal: "fixedPrincipal",
  fixed_total: "fixedTotal",
  custom: "custom",
  balloon: "balloon",
  partial_balloon: "partialBalloon"
};


type SingleDebtSettingsProps = {
  segments: DebtSegment[];
  onEditClick: () => void;
};

export const SingleDebtSettings: React.FC<SingleDebtSettingsProps> = ({ segments, onEditClick }) => {
  const { t } = useTranslation("debt");
  const format = useFormatDateHook();
  const dealDefaultCurrency = useDealDefaultCurrencySelect();

  const translateIndex = (value: string | null) => {
    if (!value) return t("linkage.unlinked");
    const key = debtIndexNames[value];
    return key ? t(key, { defaultValue: key }) : value;
  };

  const SECTION_LABELS: Record<string, string> = {
    debt: t("debtSection.title"),
    principal: t("principalSection.title"),
    interest: t("interestSection.title")
  };

  const FIELD_LABELS: Record<string, string> = {
    loanAmount: t("settings.loanAmount"),
    paymentTerms: t("settings.paymentTerms"),
    dateOfIssue: t("settings.dateOfIssue"),
    maturityDate: t("settings.maturityDate"),
    numberOfMonths: t("settings.numberOfMonths"),
    linkage: t("settings.linkage"),
    index: t("settings.index"),
    percentage: t("settings.percentage"),
    paymentDate: t("settings.paymentDate"),
    frequency: t("settings.frequency"),
    numberOfPayments: t("settings.numberOfPayments")
  };

  const settings = useMemo(() => {
    if (!segments.length)
      return {
        debt: {},
        principal: {},
        interest: {}
      };

    const segment = segments[0];
    const paymentTermKey = PAYMENT_TERM_I18N[segment.payment_term] || "custom";

    return {
      debt: {
        loanAmount: `${currencySign[dealDefaultCurrency!]}${formatNumber(segment.loan_amount)}`,
        paymentTerms: t(`paymentTerm.${paymentTermKey}`),
        dateOfIssue: format({ date: segment.start_date }),
        maturityDate: format({ date: segment.maturity_date }),
        numberOfMonths: dayjs(segment.maturity_date).diff(dayjs(segment.start_date), "month").toString()
      },
      principal: {
        linkage: translateIndex(segment.principal_reference_index),
        index: translateIndex(segment.principal_reference_index),
        percentage: segment.principal_link_percent ? segment.principal_link_percent.toString() + "%" : t("linkage.unlinked"),
        paymentDate: format({ date: segment.principal_first_payment_date }),
        frequency: segment.principal_frequency,
        numberOfPayments: segment.principal_frequency
      },
      interest: {
        linkage: translateIndex(segment.interest_reference_index),
        index: translateIndex(segment.interest_reference_index),
        percentage: segment.interest_link_percent ? segment.interest_link_percent.toString() + "%" : t("linkage.unlinked"),
        paymentDate: format({ date: segment.interest_first_payment_date }),
        frequency: segment.interest_frequency,
        numberOfPayments: segment.interest_frequency
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, t]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">{t("settings.tableTitle")}</Typography>
        <IconButton onClick={onEditClick}>
          <EditIcon />
        </IconButton>
      </Box>

      <Stack direction="row" spacing={4}>
        {["debt", "principal", "interest"].map((section) => (
          <Box flex={1} key={section}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {SECTION_LABELS[section]}
            </Typography>
            <List dense sx={{ p: 0 }}>
              {Object.entries(settings[section as keyof typeof settings]).map(([key, value]) => (
                <ListItem key={key} sx={{ py: 0 }}>
                  <ListItemText
                    sx={{ py: 0, m: 0, display: "flex", flexDirection: "row" }}
                    primary={(FIELD_LABELS[key] ?? key) + ": "}
                    secondary={value}
                    slotProps={{
                      primary: {
                        color: "primary.main",
                        fontSize: "0.875rem",
                        display: "inline"
                      },
                      secondary: {
                        color: "text.primary",
                        display: "inline",
                        ml: 1
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </Stack>
    </>
  );
};
